import { api } from '../api/endpoints.js';
import { openTopupSheet } from '../ui/bottom-sheet-topup.js';
import { openLeaderboardSheet } from '../ui/sheet-leaderboard.js';
import { openReferralSheet } from '../ui/sheet-referral.js';
import { openAirdropSheet } from '../ui/sheet-airdrop.js';
import { mainButton, backButton, haptic } from '../tg.js';

// Menu — entry scene. Layout (top → bottom):
//   [⭐ balance+]   [👤 name]      (top bar)
//   subtitle · H1 · version         (hero)
//   resume card (if active session) (tappable)
//   PRIMARY CTA button              (always visible — main interaction)
//   featured waifus row             (horizontal scroll)
//
// Telegram MainButton is wired as DUPLICATE of in-page CTA (works either way).
// We don't hide the in-page button when MainButton exists — too easy to miss.
export const menuScene = {
  async mount(root, ctx) {
    const u = ctx.state.user || {};
    backButton.hide();
    mainButton.hide(); // safety: clear any leftover MainButton from prior scenes

    // 1. Kick session check + packs load in BG (concurrent).
    const sessionPromise = api.getCurrentSession().catch(() => ({ session: null }));
    const packsPromise = api.listPacks().catch(() => ({ packs: [] }));

    // BG = first pack cover (cached across scene revisits)
    let bgUrl = ctx.state.menuBgUrl || null;
    if (!bgUrl) {
      try {
        const data = await packsPromise;
        const first = (data?.packs || [])[0];
        if (first?.cover_image_url || first?.cover_url) {
          bgUrl = first.cover_image_url || first.cover_url;
          ctx.state.menuBgUrl = bgUrl;
        }
      } catch {}
    }

    root.innerHTML = `
      <div class="menu">
        <div class="bg" ${bgUrl ? `style="background-image:url('${bgUrl}')"` : ''}></div>
        <div class="bg-fade"></div>
        <div class="lights">
          <div class="lightLeft"></div>
          <div class="lightRight"></div>
        </div>

        <div class="content">
          <!-- Top bar -->
          <div class="top-row">
            <div class="pill-base balance-pill" id="balance-pill" role="button" aria-label="Buy stars">
              <span class="icon">⭐</span>
              <span class="neon-gold" id="balance-num">${u.star_balance ?? 0}</span>
              <span class="plus">+</span>
            </div>
            <div class="top-actions">
              <button class="icon-btn" id="btn-lb" aria-label="Leaderboard">🏆</button>
              <button class="icon-btn icon-btn-airdrop" id="btn-airdrop" aria-label="Airdrop">
                💎
                <span class="icon-btn-dot"></span>
              </button>
              <button class="icon-btn" id="btn-invite" aria-label="Invite friends">💌</button>
            </div>
          </div>

          <!-- Hero -->
          <div class="hero">
            <div class="hero-sub">CABARET PRESENTS</div>
            <h1 class="hero-title">BEER CATCH<br/>PARTY</h1>
          </div>

          <!-- Resume card (active session) — tappable -->
          <button class="resume-card" id="resume-card" hidden>
            <div class="resume-cover" id="resume-cover"></div>
            <div class="resume-meta">
              <div class="resume-status" id="resume-status">RESUME GAME</div>
              <div class="resume-pack" id="resume-pack">…</div>
              <div class="resume-dots" id="resume-dots"></div>
            </div>
            <div class="resume-arrow">▶</div>
          </button>

          <!-- PRIMARY CTA — always visible -->
          <button class="cta-main" id="btn-start">
            <span class="cta-label" id="cta-label">TAP TO START</span>
            <span class="cta-arrow">→</span>
          </button>

          <!-- Featured waifus carousel -->
          <div class="featured" id="featured" hidden>
            <div class="featured-header">
              <span class="featured-label">FEATURED WAIFUS</span>
              <span class="featured-more">All ›</span>
            </div>
            <div class="featured-scroll" id="featured-scroll"></div>
          </div>
        </div>
      </div>`;

    const balancePill = root.querySelector('#balance-pill');
    const balanceNum  = root.querySelector('#balance-num');
    const resumeCard  = root.querySelector('#resume-card');
    const resumeCover = root.querySelector('#resume-cover');
    const resumeStatus = root.querySelector('#resume-status');
    const resumePack  = root.querySelector('#resume-pack');
    const resumeDots  = root.querySelector('#resume-dots');
    const ctaBtn      = root.querySelector('#btn-start');
    const ctaLabel    = root.querySelector('#cta-label');
    const featured    = root.querySelector('#featured');
    const featuredScroll = root.querySelector('#featured-scroll');

    // 2a. Top-bar action icons
    root.querySelector('#btn-lb').onclick = () => {
      haptic('light');
      openLeaderboardSheet(root);
    };
    root.querySelector('#btn-airdrop').onclick = () => {
      haptic('light');
      openAirdropSheet(root);
    };
    root.querySelector('#btn-invite').onclick = async () => {
      haptic('light');
      const result = await openReferralSheet(root);
      // Refresh balance after sheet closes — user may have just earned a referral bonus.
      if (result?.status === 'closed') {
        try {
          const me = await api.getMe();
          ctx.state.user = me;
          balanceNum.textContent = me.star_balance ?? 0;
        } catch {}
      }
    };

    // 2b. Tap balance pill → topup sheet
    balancePill.onclick = async () => {
      haptic('light');
      const cur = ctx.state.user?.star_balance ?? 0;
      const result = await openTopupSheet(root, { balance: cur });
      if (result?.status === 'paid' || result?.status === 'pending') {
        try {
          const me = await api.getMe();
          ctx.state.user = me;
          balanceNum.textContent = me.star_balance ?? 0;
        } catch (e) { console.warn('refresh balance failed', e); }
      }
    };

    // 3. Start / Continue shared handler
    let sess = null;
    const startGame = async () => {
      haptic('medium');
      if (!sess) return ctx.goto('packs');
      ctx.state.pack = {
        id: sess.pack.id,
        name: sess.pack.name,
        cover_url: sess.pack.cover_image_url,
        cover_image_url: sess.pack.cover_image_url,
        total_slots: sess.pack.total_slots
      };
      ctx.state.activeSession = sess;
      ctx.state.startWithRespawnModal = sess.status === 'pending_respawn';
      ctx.goto('game');
    };

    // In-page CTA — sole entry point (no native MainButton)
    ctaBtn.onclick = startGame;
    resumeCard.onclick = startGame;

    const setCtaText = (text) => { ctaLabel.textContent = text; };

    // 5. Resolve session → update resume card + CTA text + flair
    sessionPromise.then((resp) => {
      sess = resp?.session || null;
      if (!sess) {
        setCtaText('TAP TO START');
        ctaBtn.classList.add('flair-new');
        return;
      }
      const total = sess.pack?.total_slots ?? 5;
      const unlocked = sess.unlocked_count ?? 0;
      const cover = sess.pack?.cover_image_url;
      if (cover) resumeCover.style.backgroundImage = `url('${cover}')`;
      resumePack.textContent = sess.pack?.name || 'Session';
      const isDead = sess.status === 'pending_respawn';
      resumeStatus.textContent = isDead ? 'YOU DIED · TAP TO CONTINUE' : 'TAP TO RESUME';
      resumeStatus.classList.toggle('danger', isDead);
      resumeDots.innerHTML = Array.from({ length: total }, (_, i) =>
        `<span class="d ${i < unlocked ? 'on' : ''}"></span>`
      ).join('');
      resumeCard.hidden = false;
      setCtaText(isDead ? 'CONTINUE GAME' : 'RESUME GAME');
      ctaBtn.classList.add(isDead ? 'flair-danger' : 'flair-resume');
    });

    // 6. Resolve packs → render featured carousel
    packsPromise.then((data) => {
      const packs = data?.packs || [];
      if (packs.length === 0) return;
      featuredScroll.innerHTML = packs.map((p) => {
        const cover = p.cover_image_url || p.cover_url || '';
        return `
          <button class="featured-card" data-pack-id="${p.id}">
            <div class="featured-cover" style="background-image:url('${cover}')"></div>
            <div class="featured-name">${p.name}</div>
          </button>`;
      }).join('');
      featuredScroll.querySelectorAll('.featured-card').forEach((card) => {
        card.onclick = () => {
          haptic('selection');
          ctx.goto('packs');
        };
      });
      featured.hidden = false;
    });
  },

  async destroy() {
    mainButton.hide();
  }
};
